import { useEffect, useState } from 'react';
import { styled } from '@mui/material';
import { useLazyQuery, useMutation } from '@apollo/client';
import { graphql } from '../../../gql';
import { useAuth } from '@calefy-inc/authentication';
import LoadingScreen from '../../ManagementPanel/components/LoadingScreen';
import { DisplayInlineError } from '../../common/ErrorHandling';
import { CreateNewAdvisorButton } from '../../ManagementPanel/pages/dashboard/CompanyAdvisors/CreateNewAdvisorButton';
import { isNotNullish } from '../../../util';
import { ArrayElement } from '@calefy-inc/utilityTypes';
import { PolicystreamAdvisorsAdminQuery } from '../../../gql/graphql';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { POLICYSTREAM_INSURANCE_COMPANIES } from './PolicyStreamInsuranceCompanies';
import { BackendInsuranceCompany } from './PolicyStreamInsuranceCompanies';

type Advisor = NonNullable<
  ArrayElement<PolicystreamAdvisorsAdminQuery['allPolicystreamAdvisors']>
>;

export const PolicyStreamAdvisors = () => {
  // query the advisor data from the backend
  const { token } = useAuth();
  const [getAdvisors, { data, loading, error }] = useLazyQuery(
    POLICYSTREAM_ADVISORS_ADMIN,
  );
  const [getInsurers, { data: insurerData }] = useLazyQuery(
    POLICYSTREAM_INSURANCE_COMPANIES,
  );
  const [advisors, setAdvisors] = useState<Array<Advisor>>([]);

  useEffect(() => {
    if (token) {
      getAdvisors({ variables: { token } });
      getInsurers({ variables: { token } });
    }
  }, [token]);

  useEffect(() => {
    if (data?.allPolicystreamAdvisors) {
      setAdvisors(data.allPolicystreamAdvisors.filter(isNotNullish));
    }
  }, [data]);

  return (
    <PolicyStreamAdivsorsContainer>
      {loading ? <LoadingScreen /> : null}
      <DisplayInlineError error={error} />
      <CreateNewAdvisorButton />
      {advisors.length > 0 ? (
        advisors.map((advisor) => (
          <DisplayAdvisor
            key={advisor.id}
            advisor={advisor}
            insurers={(
              insurerData?.policystreamInsuranceCompanies || []
            ).filter(isNotNullish)}
          />
        ))
      ) : (
        <NoAdvisorsComponent />
      )}
    </PolicyStreamAdivsorsContainer>
  );
};

interface DisplayAdvisorProps {
  advisor: Advisor;
  insurers: Array<BackendInsuranceCompany>;
}
const DisplayAdvisor = ({ advisor, insurers }: DisplayAdvisorProps) => {
  return (
    <Accordion>
      <AccordionSummary>
        {advisor.name} ({advisor.organization.name})
      </AccordionSummary>
      <AccordionDetails>
        <ul>
          <li>Name: {advisor.name}</li>
          <li>Organization: {advisor.organization.name}</li>
          {insurers.length > 0 ? (
            <li>
              Insurers:
              <ul>
                {insurers.map((insurer) => (
                  <li key={insurer.id}>
                    <DisplayAdvisorInsurer
                      advisor={advisor}
                      insurer={insurer}
                      included={
                        !!advisor.insurers.find(
                          // @ts-expect-error
                          (advisorInsurer) => advisorInsurer.id === insurer.id,
                        )
                      }
                    />
                  </li>
                ))}
              </ul>
            </li>
          ) : null}
        </ul>
      </AccordionDetails>
    </Accordion>
  );
};

interface DisplayAdvisorInsurerProps {
  advisor: Advisor;
  insurer: BackendInsuranceCompany;
  included: boolean;
}
const DisplayAdvisorInsurer = ({
  advisor,
  insurer,
  included,
}: DisplayAdvisorInsurerProps) => {
  const { token } = useAuth();
  const [updateInsuranceCompany, { error }] = useMutation(
    UPDATE_POLICYSTREAM_ADVISOR_INSURANCE_COMPANIES,
    {
      refetchQueries: [
        { query: POLICYSTREAM_ADVISORS_ADMIN, variables: { token } },
      ],
    },
  );
  const [checked, setChecked] = useState<boolean>(included);
  const [errorOpen, setErrorOpen] = useState<boolean>(false);

  useEffect(() => {
    if (checked !== included) {
      updateInsuranceCompany({
        variables: {
          token,
          advisorId: advisor.id,
          changes: [{ insurerId: insurer.id, include: checked }],
        },
      });
    }
  }, [checked, included]);

  useEffect(() => {
    if (error) {
      setErrorOpen(true);
      setChecked(included);
    }
  }, [error]);

  return (
    <>
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
        }
        label={insurer.displayName}
      />
      <AdvisorInsuranceErrorDisplay
        open={errorOpen}
        onClose={() => setErrorOpen(false)}
        error={error}
      />
    </>
  );
};

interface AdvisorInsuranceErrorDisplayProps {
  open: boolean;
  onClose: () => void;
  error?: Error;
}
const AdvisorInsuranceErrorDisplay = ({
  open,
  onClose,
  error,
}: AdvisorInsuranceErrorDisplayProps) => {
  return (
    <Dialog open={open}>
      <DialogTitle>Error Updating Insurer</DialogTitle>
      <DialogContent>
        <DisplayInlineError error={error}>
          <Typography>
            Error updating advisor / insurer relationship:
          </Typography>
        </DisplayInlineError>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};

const NoAdvisorsComponent = () => {
  return <Typography>No advisors found.</Typography>;
};

const PolicyStreamAdivsorsContainer = styled('div')({});

/** Cross-tenant list — same data the old `policystreamAdvisors` query returned before org scoping. */
const POLICYSTREAM_ADVISORS_ADMIN = graphql(`
  query policystreamAdvisorsAdmin($token: String!) {
    allPolicystreamAdvisors(token: $token) {
      id
      organization {
        id
        name
      }
      name
      insurers {
        id
        displayName
      }
    }
  }
`);

const UPDATE_POLICYSTREAM_ADVISOR_INSURANCE_COMPANIES = graphql(`
  mutation updatePolicystreamAdvisorInsuranceCompanies(
    $token: String!
    $advisorId: ID!
    $changes: [AdvisorInsurerChangeInput!]!
  ) {
    updatePolicystreamAdvisorInsuranceCompanies(
      token: $token
      advisorId: $advisorId
      changes: $changes
    ) {
      advisor {
        id
        insurers {
          id
          displayName
        }
      }
    }
  }
`);
